import { Color, Radius, Spacing } from 'zens-storybook'
import { StyleSheet, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { ApiError, useStore } from 'zens-store'
import { Suspense, useEffect, useState } from 'react'
import { useRouter, useNumberParams } from 'zens-router'
import { CompanyHeader, CompanyWebhookSettingsForm, LayoutHeader, WebhookWorkspaceSelection } from '@dms/component'
import { usePermissions } from '@dms/hook'

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    margin: Spacing.L,
    padding: Spacing.L,
    backgroundColor: Color.simplyWhite,
    borderRadius: Radius.M,
  },
})

const CompanyWebhook = () => {
  const router = useRouter()
  const { api, message } = useStore()
  const [{ companyId }] = useNumberParams()
  const { isSysAdmin } = usePermissions(companyId)
  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (!isSysAdmin) {
      router.push(`/companies/${companyId}/detail`)
    }
  }, [companyId, isSysAdmin, router])

  const save = async (webhookSelections: WebhookWorkspaceSelection[]) => {
    setLoading(true)

    try {
      await Promise.all(
        webhookSelections.map((selection) =>
          api.put(`company/${companyId}/webhooks/${selection.webhookId}/workspaces`, {
            workspaces: selection.workspaceIds,
          }),
        ),
      )

      message.add({
        message: t('Webhook settings were updated successfully'),
        status: 'success',
      })
    } catch (error) {
      const { message: errorMessage } = error as ApiError
      message.add({
        message: `${t('Webhook settings could not be saved')}. ${errorMessage}`,
        status: 'error',
      })
      return
    } finally {
      setLoading(false)
    }

    router.push(`/companies/${companyId}/detail`)
  }

  if (!isSysAdmin) return null

  return (
    <View style={styles.container}>
      <Suspense fallback={<LayoutHeader.Skeleton breadcrumb title />}>
        <CompanyHeader companyId={companyId} title={t('Webhooks')} breadcrumbItems={[{ title: t('Webhooks') }]} />
      </Suspense>
      <View style={styles.content}>
        <Suspense fallback={<CompanyWebhookSettingsForm.Skeleton />}>
          <CompanyWebhookSettingsForm companyId={companyId} onSave={save} loading={loading} />
        </Suspense>
      </View>
    </View>
  )
}

export default CompanyWebhook
